import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const modalDialog = document.querySelector('.quick-add-modal');

    if (!productGrid || !modalContent) return;

    const useMobileLayout = this.#isMobileQuickAddLayout();

    if (useMobileLayout) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
    modalContent.classList.toggle('quick-add-modal__content--mobile', useMobileLayout);
    modalDialog?.classList.toggle('quick-add-modal--mobile', useMobileLayout);
    this.#applyMobileModalLayout(modalContent, modalDialog, useMobileLayout);

    this.#syncVariantSelection(modalContent);
  }

  #isMobileQuickAddLayout() {
    const visualViewportWidth = window.visualViewport?.width || window.innerWidth;
    const screenWidth = window.screen?.width || window.innerWidth;

    return isMobileBreakpoint() || visualViewportWidth < 750 || screenWidth < 750;
  }

  /**
   * Applies the mobile layout directly so production Safari cannot keep the modal in its desktop strip layout.
   * @param {HTMLElement} modalContent
   * @param {Element | null} modalDialog
   * @param {boolean} useMobileLayout
   */
  #applyMobileModalLayout(modalContent, modalDialog, useMobileLayout) {
    const managedElements = [
      modalDialog,
      modalContent,
      modalContent.parentElement?.querySelector('.quick-add-modal__close'),
      modalContent.querySelector('.product-information__media'),
      modalContent.querySelector('.product-header'),
      modalContent.querySelector('.product-details'),
      modalContent.querySelector('.product-details > .group-block'),
      modalContent.querySelector('variant-picker'),
      modalContent.querySelector('product-form-component'),
      modalContent.querySelector('.product-form-buttons'),
    ];

    for (const element of managedElements) {
      if (element instanceof HTMLElement) this.#clearQuickAddStyles(element);
    }

    if (!useMobileLayout || !(modalDialog instanceof HTMLElement)) return;

    this.#setQuickAddStyles(modalDialog, {
      position: 'fixed',
      top: 'auto',
      left: 'auto',
      right: '0.75rem',
      bottom: 'max(1rem, env(safe-area-inset-bottom))',
      translate: 'none',
      display: 'flex',
      'flex-direction': 'column',
      width: 'auto',
      'min-width': '0',
      'max-width': 'calc(100dvw - 1.5rem)',
      height: 'auto',
      'max-height': 'calc(100dvh - 2rem)',
      margin: '0',
      overflow: 'hidden',
      'box-sizing': 'border-box',
    });

    this.#setQuickAddStyles(modalContent, {
      display: 'grid',
      'grid-template-columns': 'clamp(4.5rem, 24dvw, 7rem) minmax(0, 1fr)',
      'grid-auto-rows': 'auto',
      'align-items': 'start',
      gap: '0.75rem',
      width: '100%',
      'min-width': '0',
      'max-width': 'none',
      height: 'auto',
      'max-height': 'inherit',
      padding: '0.75rem',
      overflow: 'auto',
      'box-sizing': 'border-box',
    });

    const closeButton = modalContent.parentElement?.querySelector('.quick-add-modal__close');
    if (closeButton instanceof HTMLElement) {
      this.#setQuickAddStyles(closeButton, {
        top: '0.5rem',
        right: '0.5rem',
      });
    }

    const media = modalContent.querySelector('.product-information__media');
    if (media instanceof HTMLElement) {
      this.#setQuickAddStyles(media, {
        position: 'static',
        'grid-column': '1',
        'grid-row': '1',
        width: '100%',
        'min-width': '0',
        'aspect-ratio': '1',
        overflow: 'hidden',
        background: '#fff',
      });
    }

    const productHeader = modalContent.querySelector('.product-header');
    if (productHeader instanceof HTMLElement) {
      this.#setQuickAddStyles(productHeader, {
        display: 'flex',
        'flex-direction': 'column',
        'grid-column': '2',
        'grid-row': '1',
        'min-width': '0',
        'padding-right': '3rem',
      });
    }

    const productDetails = modalContent.querySelector('.product-details');
    if (productDetails instanceof HTMLElement) {
      this.#setQuickAddStyles(productDetails, {
        display: 'block',
        'grid-column': '1 / -1',
        'grid-row': 'auto',
        width: '100%',
        'min-width': '0',
        height: 'auto',
        'max-height': 'none',
        overflow: 'visible',
      });
    }

    const detailsGroup = modalContent.querySelector('.product-details > .group-block');
    if (detailsGroup instanceof HTMLElement) {
      this.#setQuickAddStyles(detailsGroup, {
        width: '100%',
        'max-height': 'none',
        padding: '0',
      });
    }

    for (const element of modalContent.querySelectorAll('variant-picker, product-form-component')) {
      if (!(element instanceof HTMLElement)) continue;
      this.#setQuickAddStyles(element, {
        'grid-column': '1 / -1',
        'min-width': '0',
        width: '100%',
      });
    }

    const variantPicker = modalContent.querySelector('variant-picker');
    if (variantPicker instanceof HTMLElement) {
      this.#setQuickAddStyles(variantPicker, {
        display: 'flex',
        'flex-direction': 'column',
        gap: '0.75rem',
      });
    }

    const productFormButtons = modalContent.querySelector('.product-form-buttons');
    if (productFormButtons instanceof HTMLElement) {
      this.#setQuickAddStyles(productFormButtons, {
        width: '100%',
        'min-width': '0',
      });
    }
  }

  /** @param {HTMLElement} element */
  #clearQuickAddStyles(element) {
    const properties = [
      'position',
      'top',
      'left',
      'right',
      'bottom',
      'translate',
      'display',
      'flex-direction',
      'width',
      'min-width',
      'max-width',
      'height',
      'max-height',
      'margin',
      'overflow',
      'overflow-y',
      'box-sizing',
      'grid-template-columns',
      'grid-auto-rows',
      'align-items',
      'gap',
      'padding',
      'grid-column',
      'grid-row',
      'aspect-ratio',
      'background',
      'padding-right',
    ];

    for (const property of properties) element.style.removeProperty(property);
  }

  /**
   * @param {HTMLElement} element
   * @param {Record<string, string>} styles
   */
  #setQuickAddStyles(element, styles) {
    for (const [property, value] of Object.entries(styles)) {
      element.style.setProperty(property, value, 'important');
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const modalContent = this.querySelector('#quick-add-modal-content');
    if (modalContent) {
      modalContent.replaceChildren();
      modalContent.classList.remove('quick-add-modal__content--mobile');
    }

    this.refs.dialog.classList.remove('quick-add-modal--mobile');

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
